import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  FileText,
  Plus,
  Trash2,
  GripVertical,
  Eye,
  Upload,
  User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'Target', label: 'Target' },
  { value: 'Gamepad2', label: 'Gamepad' },
  { value: 'FileText', label: 'Document' },
  { value: 'BookOpen', label: 'Book' },
  { value: 'Brain', label: 'Brain' },
  { value: 'Users', label: 'Users' },
  { value: 'Stethoscope', label: 'Stethoscope' }
];

export default function AdminAbout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFounder, setSavingFounder] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const founderPhotoRef = useRef(null);
  
  const [content, setContent] = useState({
    hero_badge: '',
    hero_title: '',
    hero_subtitle: '',
    mission_title: '',
    mission_paragraph_1: '',
    mission_paragraph_2: '',
    features_title: '',
    features: [],
    benefits_title: '',
    benefits: [],
    footer_cta_title: '',
    footer_cta_subtitle: ''
  });

  const [founder, setFounder] = useState({
    name: '',
    credentials: '',
    bio: '',
    photo_url: null
  });

  useEffect(() => {
    fetchContent();
    fetchFounder();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/about/content`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setContent(data);
      } else {
        throw new Error('Failed to fetch content');
      }
    } catch (err) {
      toast.error('Failed to load About page content');
    } finally {
      setLoading(false);
    }
  };

  const fetchFounder = async () => {
    try {
      const res = await fetch(`${API}/api/admin/founder`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFounder(data);
      }
    } catch (err) {
      console.error('Failed to fetch founder info:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/about/content`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success('About page content saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFounder = async () => {
    setSavingFounder(true);
    try {
      const res = await fetch(`${API}/api/admin/founder`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: founder.name,
          credentials: founder.credentials,
          bio: founder.bio
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success('Founder info saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingFounder(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/admin/founder/upload-photo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to upload');
      }

      const data = await res.json();
      setFounder(prev => ({ ...prev, photo_url: data.photo_url }));
      toast.success('Photo uploaded successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      const res = await fetch(`${API}/api/admin/founder/delete-photo`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setFounder(prev => ({ ...prev, photo_url: null }));
        toast.success('Photo deleted');
      }
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return `${API}/api${url}`;
    return `${API}/api/${url}`;
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const updateFeature = (index, field, value) => {
    setContent(prev => {
      const features = [...prev.features];
      features[index] = { ...features[index], [field]: value };
      return { ...prev, features };
    });
  };

  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [...prev.features, { icon: 'Target', title: '', description: '' }]
    }));
  };

  const removeFeature = (index) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateBenefit = (index, value) => {
    setContent(prev => {
      const benefits = [...prev.benefits];
      benefits[index] = value;
      return { ...prev, benefits };
    });
  };

  const addBenefit = () => {
    setContent(prev => ({
      ...prev,
      benefits: [...prev.benefits, '']
    }));
  };

  const removeBenefit = (index) => {
    setContent(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-slate-600 hover:text-slate-900"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-600" />
                <h1 className="text-xl font-bold text-slate-900">Edit About Page</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => window.open('/about', '_blank')}
                className="border-slate-200"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="save-about-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Hero Section */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Hero Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">Badge Text</Label>
              <Input
                value={content.hero_badge || ''}
                onChange={(e) => updateField('hero_badge', e.target.value)}
                placeholder="e.g., Medical Education Platform"
                className="mt-1 bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-700">Title</Label>
              <Input
                value={content.hero_title || ''}
                onChange={(e) => updateField('hero_title', e.target.value)}
                placeholder="e.g., About MedStudyPro"
                className="mt-1 bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-700">Subtitle</Label>
              <Textarea
                value={content.hero_subtitle || ''}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                placeholder="Main description paragraph..."
                className="mt-1 min-h-[80px] bg-white border-slate-200"
              />
            </div>
          </CardContent>
        </Card>

        {/* Mission Section */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Mission Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">Section Title</Label>
              <Input
                value={content.mission_title || ''}
                onChange={(e) => updateField('mission_title', e.target.value)}
                placeholder="e.g., Our Mission"
                className="mt-1 bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-700">First Paragraph</Label>
              <Textarea
                value={content.mission_paragraph_1 || ''}
                onChange={(e) => updateField('mission_paragraph_1', e.target.value)}
                placeholder="First mission paragraph..."
                className="mt-1 min-h-[100px] bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-700">Second Paragraph</Label>
              <Textarea
                value={content.mission_paragraph_2 || ''}
                onChange={(e) => updateField('mission_paragraph_2', e.target.value)}
                placeholder="Second mission paragraph..."
                className="mt-1 min-h-[100px] bg-white border-slate-200"
              />
            </div>
          </CardContent>
        </Card>

        {/* Features Section */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-slate-900">Features Section</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={addFeature}
              className="border-slate-200"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Feature
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">Section Title</Label>
              <Input
                value={content.features_title || ''}
                onChange={(e) => updateField('features_title', e.target.value)}
                placeholder="e.g., What You Get"
                className="mt-1 bg-white border-slate-200"
              />
            </div>
            
            <div className="space-y-3">
              {(content.features || []).map((feature, idx) => (
                <div key={idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center text-slate-400">
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div className="flex-1 grid grid-cols-12 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Icon</Label>
                      <Select
                        value={feature.icon || 'Target'}
                        onValueChange={(v) => updateFeature(idx, 'icon', v)}
                      >
                        <SelectTrigger className="mt-1 bg-white border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ICON_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Input
                        value={feature.title || ''}
                        onChange={(e) => updateFeature(idx, 'title', e.target.value)}
                        placeholder="Feature title"
                        className="mt-1 bg-white border-slate-200"
                      />
                    </div>
                    <div className="col-span-6">
                      <Label className="text-xs text-slate-500">Description</Label>
                      <Input
                        value={feature.description || ''}
                        onChange={(e) => updateFeature(idx, 'description', e.target.value)}
                        placeholder="Feature description..."
                        className="mt-1 bg-white border-slate-200"
                      />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature(idx)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Benefits Section */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-slate-900">Benefits Section</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={addBenefit}
              className="border-slate-200"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Benefit
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">Section Title</Label>
              <Input
                value={content.benefits_title || ''}
                onChange={(e) => updateField('benefits_title', e.target.value)}
                placeholder="e.g., Why Choose MedStudyPro?"
                className="mt-1 bg-white border-slate-200"
              />
            </div>
            
            <div className="space-y-2">
              {(content.benefits || []).map((benefit, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    value={benefit || ''}
                    onChange={(e) => updateBenefit(idx, e.target.value)}
                    placeholder="Enter benefit..."
                    className="bg-white border-slate-200"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBenefit(idx)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* About the Founder Section */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-slate-900">About the Founder</CardTitle>
            <Button
              onClick={handleSaveFounder}
              disabled={savingFounder}
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {savingFounder ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Founder Info
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Photo Upload */}
            <div>
              <Label className="text-slate-700 mb-2 block">Founder Photo</Label>
              <div className="flex items-start gap-4">
                <div className="w-32 h-32 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                  {founder.photo_url ? (
                    <img 
                      src={getPhotoUrl(founder.photo_url)} 
                      alt="Founder" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-12 h-12 text-slate-300" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={founderPhotoRef}
                    onChange={handlePhotoUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => founderPhotoRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="border-slate-200"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Photo
                  </Button>
                  {founder.photo_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeletePhoto}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  )}
                  <p className="text-xs text-slate-500">JPEG, PNG, or WebP. Max 5MB.</p>
                </div>
              </div>
            </div>

            {/* Founder Name */}
            <div>
              <Label className="text-slate-700">Founder Name</Label>
              <Input
                value={founder.name || ''}
                onChange={(e) => setFounder(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Dr. Jane Smith"
                className="mt-1 bg-white border-slate-200"
              />
            </div>

            {/* Credentials/Title */}
            <div>
              <Label className="text-slate-700">Credentials / Title</Label>
              <Input
                value={founder.credentials || ''}
                onChange={(e) => setFounder(prev => ({ ...prev, credentials: e.target.value }))}
                placeholder="e.g., DNP, AGACNP-BC"
                className="mt-1 bg-white border-slate-200"
              />
            </div>

            {/* Bio */}
            <div>
              <Label className="text-slate-700">Bio</Label>
              <Textarea
                value={founder.bio || ''}
                onChange={(e) => setFounder(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Write a paragraph about the founder's background, experience, and vision..."
                className="mt-1 min-h-[150px] bg-white border-slate-200"
              />
              <p className="text-xs text-slate-500 mt-1">This text will appear on the About page. Use line breaks for multiple paragraphs.</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer CTA Section */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Footer Call-to-Action</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">CTA Title</Label>
              <Input
                value={content.footer_cta_title || ''}
                onChange={(e) => updateField('footer_cta_title', e.target.value)}
                placeholder="e.g., Ready to Elevate Your Clinical Knowledge?"
                className="mt-1 bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-700">CTA Subtitle</Label>
              <Textarea
                value={content.footer_cta_subtitle || ''}
                onChange={(e) => updateField('footer_cta_subtitle', e.target.value)}
                placeholder="Supporting text for the CTA..."
                className="mt-1 min-h-[60px] bg-white border-slate-200"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button (bottom) */}
        <div className="flex justify-end pb-8">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
